import { ActionIcon, Menu } from '@mantine/core';
import {
  IconCircleCheck,
  IconCircleX,
  IconCopyPlus,
  IconDeviceFloppy,
  IconEdit,
  IconEye,
  IconFileImport,
  IconLock,
  IconLockOpen,
  IconMail,
  IconMenu2,
  IconPencil,
  IconRosetteDiscountCheck,
  IconSettings,
  IconSignature,
  IconTrash,
} from '@tabler/icons-react';
import { useIntl } from 'react-intl';
import { File as FileType } from '@/clients/pages/FolderPage/types';
import { FileAction } from '@/users/components/UserFolders/types';

interface FileActionsMenuProps {
  file: FileType;
  onPublish: () => void;
  onLock: () => void;
  onSign: () => void;
  onVerify: () => void;
  onPreview: () => void;
  onDownload: () => void;
  onSendEmail: () => void;
  onImportFields: () => void;
  onEditData: () => void;
  onEditStructure: () => void;
  onDuplicate: () => void;
  onRename: () => void;
  onDelete: () => void;
}

export const FileActionsMenu = ({
  file,
  onPublish,
  onLock,
  onSign,
  onVerify,
  onPreview,
  onDownload,
  onSendEmail,
  onImportFields,
  onEditData,
  onEditStructure,
  onDuplicate,
  onRename,
  onDelete,
}: FileActionsMenuProps) => {
  const intl = useIntl();

  const fileActions: FileAction[] = [
    {
      type: 'label',
      label: intl.formatMessage({ id: 'actions' }),
    },
    ...(!file.isSigned
      ? ([
          {
            type: 'item',
            label: file.isPublished
              ? intl.formatMessage({ id: 'unpublish' })
              : intl.formatMessage({ id: 'publish' }),
            icon: file.isPublished ? (
              <IconCircleX size={16} />
            ) : (
              <IconCircleCheck size={16} />
            ),
            onClick: onPublish,
          },
        ] as FileAction[])
      : []),
    ...(!file.isSigned
      ? ([
          {
            type: 'item',
            label: file.isLocked
              ? intl.formatMessage({ id: 'unlock' })
              : intl.formatMessage({ id: 'lock' }),
            icon: file.isLocked ? (
              <IconLockOpen size={16} />
            ) : (
              <IconLock size={16} />
            ),
            onClick: onLock,
          },
        ] as FileAction[])
      : []),
    ...(!file.isVerified && !file.isSigned
      ? ([
          {
            type: 'item',
            label: intl.formatMessage({ id: 'verify' }),
            icon: <IconRosetteDiscountCheck size={16} />,
            onClick: onVerify,
          },
        ] as FileAction[])
      : []),
    ...(!file.isSigned
      ? ([
          {
            type: 'item',
            label: intl.formatMessage({ id: 'sign' }),
            icon: <IconSignature size={16} />,
            onClick: onSign,
          },
        ] as FileAction[])
      : []),
    {
      type: 'item',
      label: intl.formatMessage({ id: 'preview' }),
      icon: <IconEye size={16} />,
      onClick: onPreview,
    },
    {
      type: 'item',
      label: intl.formatMessage({ id: 'downloadPdf' }),
      icon: <IconDeviceFloppy size={16} />,
      onClick: onDownload,
    },
    {
      type: 'item',
      label: intl.formatMessage({ id: 'sendViaEmail' }),
      icon: <IconMail size={16} />,
      onClick: onSendEmail,
    },
    {
      type: 'label',
      label: intl.formatMessage({ id: 'file' }),
    },
    {
      type: 'item',
      label: intl.formatMessage({ id: 'importFields' }),
      icon: <IconFileImport size={16} />,
      onClick: onImportFields,
    },
    {
      type: 'item',
      label: intl.formatMessage({ id: 'editData' }),
      icon: <IconEdit size={16} />,
      onClick: onEditData,
    },
    {
      type: 'item',
      label: intl.formatMessage({ id: 'editStructure' }),
      icon: <IconSettings size={16} />,
      onClick: onEditStructure,
    },
    {
      type: 'divider',
    },
    ...(file.type === 'ANNEX'
      ? ([
          {
            type: 'item',
            label: intl.formatMessage({ id: 'duplicate' }),
            icon: <IconCopyPlus size={16} />,
            onClick: onDuplicate,
          },
        ] as FileAction[])
      : []),
    ...(!file.isSigned
      ? ([
          {
            type: 'item',
            label: intl.formatMessage({ id: 'rename' }),
            icon: <IconPencil size={16} />,
            onClick: onRename,
          },
        ] as FileAction[])
      : []),
    ...(file.type === 'ANNEX' && !file.isSigned
      ? ([
          {
            type: 'divider',
          },
        ] as FileAction[])
      : []),
    {
      type: 'item',
      label: intl.formatMessage({ id: 'delete' }),
      icon: <IconTrash size={16} />,
      color: 'red',
      onClick: onDelete,
    },
  ];

  return (
    <Menu position="bottom-end" withinPortal withArrow>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray">
          <IconMenu2 size={16} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        {fileActions.map((action, index) =>
          action.type === 'label' ? (
            <Menu.Label key={index}>{action.label}</Menu.Label>
          ) : action.type === 'divider' ? (
            <Menu.Divider key={index} />
          ) : (
            <Menu.Item
              key={index}
              leftSection={action.icon}
              onClick={action.onClick}
              color={action.color}
            >
              {action.label}
            </Menu.Item>
          )
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
